import React, { useEffect, useState, useRef } from 'react';
import { Crisp } from 'crisp-sdk-web';
import mixpanel from 'mixpanel-browser';
import { useDispatch } from 'react-redux';
import { NavLink, useParams, useHistory } from 'react-router-dom';
import { addMixPanelEvent } from '../store/actions';
import { FREE_AND_PREVIEW_PLANS } from '../constants/constants';
import { UPGRADE_DASHBOARD } from '../constants/hotjar-events';
import { Settings } from 'lucide-react';
import { useGlobalStore } from '../zustand-store/useGlobalStore';
import { useOrganisation, useOrganisationUsage } from '../http-hooks/organisation/useOrganisation';
import { useIntegration, useIntegrations } from '../http-hooks/integration/useIntegration';
import { Ico, Logo } from './ui/Icons';
import { C } from '../styles/tokens';
import { Auth } from '../utils/amplifyWrapper';
import * as Sentry from '@sentry/react';

// ── "More tools" flyout items ─────────────────────────────────
const MORE_TOOLS = [
  {
    label: 'Audio Native', nav: null, soon: true,
    svg: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={C.n500} strokeWidth="1.6" strokeLinecap="round">
      <circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>,
  },
  {
    label: 'Productions', nav: null, soon: true,
    svg: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={C.n500} strokeWidth="1.6" strokeLinecap="round">
      <rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
      <rect x="8" y="11" width="8" height="5" rx="1" />
    </svg>,
  },
  {
    label: 'Audiobooks', nav: null, soon: true,
    svg: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={C.n500} strokeWidth="1.6" strokeLinecap="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>,
  },
];

// ── Expandable sidebar section (top level) ────────────────────
const Expandable = ({ icon, label, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: 1 }}>
      <button
        onClick={() => setIsOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 9,
          padding: '6px 10px', borderRadius: 7, border: 'none',
          background: 'transparent', color: C.n600,
          fontSize: 13.5, fontWeight: 500, cursor: 'pointer', textAlign: 'left',
          transition: 'background .1s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = C.n50}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        {icon}
        <span style={{ flex: 1 }}>{label}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.n400} strokeWidth="2.5">
          <path d={isOpen ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'} />
        </svg>
      </button>
      {isOpen && (
        <div style={{ paddingLeft: 24 }}>
          {children}
        </div>
      )}
    </div>
  );
};

// ── Nested expandable (second level, no icon) ─────────────────
const SubExpandable = ({ label, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: 1 }}>
      <button
        onClick={() => setIsOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 8,
          padding: '5px 10px', borderRadius: 6, border: 'none',
          background: 'transparent', color: C.n500,
          fontSize: 12.5, fontWeight: 500, cursor: 'pointer', textAlign: 'left',
          transition: 'background .1s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = C.n50}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        <span style={{ flex: 1 }}>{label}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={C.n400} strokeWidth="2.5">
          <path d={isOpen ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'} />
        </svg>
      </button>
      {isOpen && (
        <div style={{ paddingLeft: 10 }}>
          {children}
        </div>
      )}
    </div>
  );
};

// ── Main Sidebar ──────────────────────────────────────────────
function SideBar() {
  const sidebarRef = useRef();
  const moreRef = useRef(null);
  const history = useHistory();
  const { org_id, id } = useParams();
  const dispatch = useDispatch();

  const [wsOpen, setWsOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [hovMore, setHovMore] = useState(null);

  // ── Zustand ──────────────────────────────────────────────────
  const toggleUpgradeModal = useGlobalStore(state => state.toggleUpgradeModal);
  const pageType = useGlobalStore(state => state.sideBarPageType);
  const setSideBarPageType = useGlobalStore(state => state.setSideBarPageType);
  const toggleCreateProjectModal = useGlobalStore(state => state.toggleCreateProjectModal);

  // ── Data hooks ───────────────────────────────────────────────
  const { data: organisation } = useOrganisation({ org_id });
  const { data: usage } = useOrganisationUsage({ org_id });
  const { data: integration, isLoading } = useIntegration({ org_id, integration_id: id });
  const { data: integrations = [] } = useIntegrations({ org_id });

  // Close page-type panel when clicking outside the sidebar. Reads `pageType`
  // (the zustand state) directly rather than a shadow ref, so this reacts
  // correctly no matter which caller changed it — including SideBarLayout's
  // header buttons, which set the store directly rather than through here.
  useEffect(() => {
    const fn = e => {
      const bc = sidebarRef.current?.getBoundingClientRect();
      if (!bc) return;
      if (pageType !== 'MENU' &&
        !(e.clientX > bc.x && e.clientX < bc.x + bc.width &&
          e.clientY > bc.y && e.clientY < bc.y + bc.height)) {
        setSideBarPageType('MENU');
      }
    };
    document.addEventListener('click', fn);
    return () => document.removeEventListener('click', fn);
  }, [pageType, setSideBarPageType]);

  // Close "More tools" flyout on outside click
  useEffect(() => {
    const handler = e => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSignOut = async () => {
    try {
      Crisp.session.reset();
      mixpanel.reset();
      await Auth.signOut();
      window.location.replace(window.location.origin);
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  // Workspace display values
  const orgName = organisation?.organisation_name || integration?.integration_name || 'My Workspace';
  const initials = orgName.charAt(0).toUpperCase();

  // Nested nav link style
  const nestedLinkStyle = {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '5px 10px', borderRadius: 6,
    color: C.n500, fontSize: 12.5, fontWeight: 500,
    textDecoration: 'none', marginBottom: 1, transition: 'background .1s',
  };

  return (
    <div className="d-flex flex-column h-100" ref={sidebarRef}>

      {/* ── Logo ── */}
      <div style={{
        padding: '13px 14px 10px', display: 'flex', alignItems: 'center', gap: 9,
        borderBottom: `1px solid ${C.n200}`, flexShrink: 0,
      }}>
        <div
          onClick={() => history.push(`/organisation/${org_id}/integration/${id}/dashboard`)}
          style={{ cursor: 'pointer' }}
        >
          <Logo size={28} />
        </div>
        <span style={{ fontSize: 15, fontWeight: 700, color: C.n900 }}>Listen2It</span>
      </div>

      {/* ── Workspace / project selector ── */}
      <div style={{ padding: '8px 10px 4px', position: 'relative', flexShrink: 0 }}>
        <button
          onClick={() => setSideBarPageType(pageType === 'INTEGRATIONS' ? 'MENU' : 'INTEGRATIONS')}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 8,
            padding: '7px 9px', borderRadius: 8,
            border: `1px solid ${pageType === 'INTEGRATIONS' ? C.p200 : C.n200}`,
            background: pageType === 'INTEGRATIONS' ? C.p50 : C.w,
            cursor: 'pointer', textAlign: 'left', transition: 'all .12s',
          }}
        >
          <div style={{
            width: 24, height: 24, borderRadius: 6,
            background: `linear-gradient(135deg,${C.p500},${C.v500})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700, color: C.w, flexShrink: 0,
          }}>
            {initials}
          </div>
          <span style={{
            fontSize: 12.5, fontWeight: 600,
            color: pageType === 'INTEGRATIONS' ? C.p600 : C.n900,
            flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {isLoading ? '…' : (integration?.integration_name || 'My Project')}
          </span>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
            stroke={pageType === 'INTEGRATIONS' ? C.p500 : C.n400} strokeWidth="2.5">
            <path d={pageType === 'INTEGRATIONS' ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'} />
          </svg>
        </button>
      </div>

      {/* ── Body ── */}
      {pageType === 'INTEGRATIONS' ? (

        <div className="sidebar-menu flex-grow-1 py-2">
          <div style={{ padding: '4px 10px 6px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.n400, textTransform: 'uppercase', letterSpacing: '.07em', padding: '4px 10px', marginBottom: 4 }}>
              Projects
            </div>
            {integrations.map((intg, i) => (
              <div key={`intg-${i}`} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 9px', borderRadius: 7,
              }}>
                <NavLink
                  to={`/organisation/${org_id}/integration/${intg.SK.split('#')[1]}/content`}
                  activeClassName="sidebar-active"
                  className="sidebar-nav-link"
                  style={{ flex: 1, fontSize: 13 }}
                  onClick={() => setSideBarPageType('MENU')}
                >
                  {intg.integration_name}
                </NavLink>
                <span
                  style={{ cursor: 'pointer', color: C.n400, flexShrink: 0 }}
                  onClick={() => history.push(`/organisation/${org_id}/integration/${intg.SK.split('#')[1]}/general`)}
                >
                  <Settings size={13} />
                </span>
              </div>
            ))}
            <div style={{ padding: '6px 10px 0' }}>
              <button
                onClick={() => { setSideBarPageType('MENU'); toggleCreateProjectModal(true); }}
                style={{
                  width: '100%', padding: '7px 10px', borderRadius: 7,
                  border: `1px dashed ${C.n300}`, background: 'transparent',
                  color: C.n500, fontSize: 12.5, fontWeight: 500, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <Ico n="plus" size={13} color={C.n400} /> New Project
              </button>
            </div>
          </div>
        </div>

      ) : (

        <div className="sidebar-menu flex-grow-1">

          {/* Upgrade prompt for free plan users */}
          {FREE_AND_PREVIEW_PLANS.includes(organisation?.plan_id) && (
            <div style={{ margin: '8px 10px', padding: '10px 12px', borderRadius: 9, background: C.p50, border: `1px solid ${C.p200}` }}>
              <div style={{ fontSize: 11, color: C.p600, fontWeight: 600, marginBottom: 6 }}>Unlock all features</div>
              <button
                data-close-sidebar
                onClick={() => {
                  window.hj('event', UPGRADE_DASHBOARD);
                  toggleUpgradeModal(true, { source: 'sidebar', isManual: true });
                }}
                style={{
                  width: '100%', padding: '6px', borderRadius: 6, border: 'none',
                  background: C.p600, color: C.w, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Upgrade Now
              </button>
            </div>
          )}

          {/* ── Primary nav ── */}
          <nav style={{ padding: '4px 10px' }}>
            <NavLink to={`/organisation/${org_id}/integration/${id}/dashboard`} activeClassName="sidebar-active" className="sidebar-nav-link">
              <Ico n="home" size={15} color={C.n400} />
              <span>Home</span>
            </NavLink>
            <NavLink to={`/organisation/${org_id}/integration/${id}/voice`} activeClassName="sidebar-active" className="sidebar-nav-link">
              <Ico n="mic" size={15} color={C.n400} />
              <span>Voices</span>
            </NavLink>
            <NavLink to={`/organisation/${org_id}/integration/${id}/dubbing`} activeClassName="sidebar-active" className="sidebar-nav-link">
              <Ico n="globe" size={15} color={C.n400} />
              <span>Dubbing</span>
              <span style={{
                marginLeft: 'auto', padding: '1px 6px', borderRadius: 100,
                background: C.n900, color: C.w,
                fontSize: 9.5, fontWeight: 700, letterSpacing: '.02em',
              }}>New</span>
            </NavLink>
            <NavLink to={`/organisation/${org_id}/integration/${id}/content`} activeClassName="sidebar-active" className="sidebar-nav-link">
              <Ico n="edit" size={15} color={C.n400} />
              <span>Text to Speech</span>
            </NavLink>

            {/* Audio Articles — hidden for now
            <Expandable icon={<Ico n="folder" size={15} color={C.n400} />} label="Audio Articles">
              <NavLink to={`/organisation/${org_id}/integration/${id}/content-audio-articles`} activeClassName="sidebar-active" className="sidebar-nav-link" style={nestedLinkStyle}>All Audios</NavLink>

              <SubExpandable label="Analytics">
                <NavLink to={`/organisation/${org_id}/integration/${id}/analytics_overview`} activeClassName="sidebar-active" className="sidebar-nav-link" style={nestedLinkStyle}>Overview</NavLink>
                <NavLink to={`/organisation/${org_id}/integration/${id}/content_analytics`} activeClassName="sidebar-active" className="sidebar-nav-link" style={nestedLinkStyle}>Audio Level</NavLink>
              </SubExpandable>

              <SubExpandable label="Integration">
                <NavLink to={`/organisation/${org_id}/integration/${id}/wordpress`} activeClassName="sidebar-active" className="sidebar-nav-link" style={nestedLinkStyle}>WordPress</NavLink>
                <NavLink to={`/organisation/${org_id}/integration/${id}/cms`} activeClassName="sidebar-active" className="sidebar-nav-link" style={nestedLinkStyle}>Other CMS</NavLink>
              </SubExpandable>

              <NavLink to={`/organisation/${org_id}/integration/${id}/player`} activeClassName="sidebar-active" className="sidebar-nav-link" style={nestedLinkStyle}>Audio Player</NavLink>
            </Expandable>
            */}
          </nav>

          {/* ── Pinned section ── */}
          <div style={{ padding: '6px 10px 0' }}>
            <div style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, color: C.n400, textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 2 }}>
              Pinned
            </div>

            <NavLink to={`/organisation/${org_id}/integration/${id}/sound-effects`} activeClassName="sidebar-active" className="sidebar-nav-link">
              <Ico n="bolt" size={15} color={C.n400} />
              <span>Sound Effects</span>
            </NavLink>
            <NavLink to={`/organisation/${org_id}/integration/${id}/music`} activeClassName="sidebar-active" className="sidebar-nav-link">
              <Ico n="music" size={15} color={C.n400} />
              <span>Music</span>
            </NavLink>
            <NavLink to={`/organisation/${org_id}/integration/${id}/voice-changer`} activeClassName="sidebar-active" className="sidebar-nav-link">
              <Ico n="refresh" size={15} color={C.n400} />
              <span>Voice Changer</span>
              <span style={{
                marginLeft: 'auto', padding: '1px 6px', borderRadius: 100,
                background: C.n900, color: C.w,
                fontSize: 9.5, fontWeight: 700, letterSpacing: '.02em',
              }}>New</span>
            </NavLink>
            <NavLink to={`/organisation/${org_id}/integration/${id}/voice-isolator`} activeClassName="sidebar-active" className="sidebar-nav-link">
              <Ico n="mic" size={15} color={C.n400} />
              <span>Voice Isolator</span>
              <span style={{
                marginLeft: 'auto', padding: '1px 6px', borderRadius: 100,
                background: C.n900, color: C.w,
                fontSize: 9.5, fontWeight: 700, letterSpacing: '.02em',
              }}>New</span>
            </NavLink>
            {/* API — hidden for now
            <NavLink to={`/organisation/${org_id}/integration/${id}/api`} activeClassName="sidebar-active" className="sidebar-nav-link">
              <Ico n="copy" size={15} color={C.n400} />
              <span>API</span>
            </NavLink>
            */}
          </div>

        </div>
      )}

      <div style={{ flex: 1 }} />

      {/* ── Bottom section ── */}
      <div style={{ padding: '6px 10px', borderTop: `1px solid ${C.n100}`, flexShrink: 0 }}>

        {/* Word usage — only shown here on mobile; desktop already shows this in the top bar */}
        {usage && (
          <div className="d-md-none" style={{ padding: '4px 10px 10px', fontSize: 12, color: C.n500 }}>
            {(usage.credits_remaining ?? usage.words_remaining)?.toLocaleString()} of {(usage.plan_credits_limit ?? usage.plan_words_limit)?.toLocaleString()} credits remaining
          </div>
        )}

        {/* Roadmap link hidden for now
        <button
          onClick={() => window.open('https://listen2it.productlane.com/roadmap', '_blank', 'noopener')}
          className="sidebar-nav-link"
          style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer' }}
        >
          <Ico n="trend" size={15} color={C.n400} />
          <span>Roadmap</span>
        </button>
        */}

        {/* Invite card — hidden for now */}

        <NavLink to={`/organisation/${org_id}/integration/${id}/billing`} activeClassName="sidebar-active" className="sidebar-nav-link">
          <Ico n="user" size={15} color={C.n400} />
          <span>Plan & Billing</span>
        </NavLink>

        <button
          data-close-sidebar
          onClick={() => {
            dispatch(addMixPanelEvent({
              orgId: org_id, integrationId: id, body: {
                eventName: "SUPPORT_CHAT_OPENED",
                eventData: { source: 'sidebar' },
              }
            }));
            Crisp.chat.open();
          }}
          className="sidebar-nav-link"
          style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer' }}
        >
          <Ico n="chat" size={15} color={C.n400} />
          <span>Support</span>
        </button>

        <button
          data-close-sidebar
          onClick={handleSignOut}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 9,
            padding: '6px 10px', borderRadius: 7, border: 'none',
            background: 'transparent', color: '#DC2626',
            fontSize: 13.5, fontWeight: 500, cursor: 'pointer', textAlign: 'left', marginBottom: 1,
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#FEF2F2'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="1.75" strokeLinecap="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
          </svg>
          <span>Sign out</span>
        </button>

        {/* Upgrade pill */}
        <button
          data-close-sidebar
          onClick={() => {
            window.hj('event', UPGRADE_DASHBOARD);
            toggleUpgradeModal(true, { source: 'sidebar', isManual: true });
          }}
          style={{
            width: '100%', marginTop: 6, padding: '9px',
            background: C.n900, color: C.w, border: 'none', borderRadius: 9,
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
          Upgrade
        </button>

      </div>
    </div>
  );
}

export default SideBar;
